import { type Api, normalisePhone, regionFromLocale } from "@rhema/client";
import { useCallback, useEffect, useState } from "react";
import type { Platform } from "../platform";
import { usePhoneEnabled } from "../session/features";
import { useSession } from "../session/session";
import { contactsChanged } from "./useContacts";

/** Someone in the address book who is not on Rhema, kept in memory for the invite list only. */
export type Invitee = { name: string; phone: string };

export type SyncState = {
  /** The phone has an address book to read. False on desktop and the web. */
  available: boolean;
  syncing: boolean;
  /** The person refused access to their contacts. */
  denied: boolean;
  error: string | null;
  invitees: Invitee[];
};

/** How often the app reads the address book again on its own. */
export const SYNC_EVERY_MS = 10 * 60 * 1000;

/** The device region, which numbers without a country code belong to. */
export function deviceRegion() {
  return regionFromLocale(globalThis.navigator?.language);
}

// Per app run. The invite list holds names and numbers, so it is never written anywhere.
let lastSync = 0;
let running: Promise<void> | null = null;
let shared: Omit<SyncState, "available"> = { syncing: false, denied: false, error: null, invitees: [] };
const listeners = new Set<() => void>();

function publish(patch: Partial<typeof shared>) {
  shared = { ...shared, ...patch };
  for (const l of listeners) l();
}

/** Forgets the last sync, for tests and after signing out. */
export function resetContactSync() {
  lastSync = 0;
  running = null;
  shared = { syncing: false, denied: false, error: null, invitees: [] };
}

/**
 * Reads the address book, matches its numbers in batches of 1,000 and saves the people on Rhema as
 * contacts. Whoever is left becomes the invite list.
 */
export async function syncContacts(platform: Platform, api: Api, { force = false } = {}): Promise<void> {
  if (!platform.readAddressBook) return;
  if (running) return running;
  if (!force && lastSync && Date.now() - lastSync < SYNC_EVERY_MS) return;
  const read = platform.readAddressBook;
  running = (async () => {
    publish({ syncing: true, error: null });
    try {
      const book = await read();
      if (!book) {
        publish({ denied: true, invitees: [] });
        return;
      }
      const region = deviceRegion();
      const byPhone = new Map<string, string>();
      for (const entry of book) {
        for (const raw of entry.phones) {
          const phone = normalisePhone(raw, region);
          if (phone && !byPhone.has(phone)) byPhone.set(phone, entry.name || phone);
        }
      }
      const matched = await api.matchAllContacts([...byPhone.keys()]);
      const onRhema = new Set(matched.map((p) => p.phone).filter(Boolean));
      const invitees = [...byPhone]
        .filter(([phone]) => !onRhema.has(phone))
        .map(([phone, name]) => ({ name, phone }))
        .sort((a, b) => a.name.localeCompare(b.name));
      lastSync = Date.now();
      publish({ denied: false, invitees });
      contactsChanged();
    } catch (err) {
      publish({ error: (err as Error).message || "Your contacts could not be checked. Try again." });
    } finally {
      publish({ syncing: false });
      running = null;
    }
  })();
  return running;
}

/** Contacts from the phone's address book. With `auto`, a sync starts when the screen opens. */
export function useContactSync(auto = false) {
  const { platform, api } = useSession();
  const [, setTick] = useState(0);
  const phone = usePhoneEnabled();
  const available = phone && Boolean(platform.readAddressBook);

  useEffect(() => {
    const listener = () => setTick((n) => n + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (auto && available) void syncContacts(platform, api);
  }, [auto, available, platform, api]);

  const refresh = useCallback(() => syncContacts(platform, api, { force: true }), [platform, api]);
  const state: SyncState = { ...shared, available };
  return { ...state, refresh };
}
